import { StyledNewProductForm } from './style';
import { Input } from '../../Input';
import { useIntl } from 'react-intl';
import React, { useEffect, useRef, useState } from 'react';
import { CategorySelector } from '../../Select/Selectors/CategorySelectors';
import { TextArea } from '../../TextArea';
import { Checkbox, Form } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useAuth } from 'hooks/useAuth';
import { FarmProps } from '../../Drawer/Drawers/DrawerCreateProduct';
import { Select, SelectOption } from 'ui';
import { ProducerLookupResponse } from 'types/ProductData';

export interface ProducerLookupParams {
  reg_nr?: string;
  farm_id?: number;
  name?: string;
  surname?: string;
  person_full_name?: string;
}

interface NewProductFormProps {
  mainCategoryValue?: number;
  subCategoryValue?: number;
  subSubCategoryValue?: number;
  productId?: number;
  farmId?: number | null;
  adminFarmId?: number | null;
  initialProducerLookup?: ProducerLookupParams | null;
  farms: FarmProps[];
  isEditMode: boolean;
  setAdminFarmId: React.Dispatch<
    React.SetStateAction<number | null | undefined>
  >;
}

const REG_NR_PATTERN = /^\d{11}$/;

interface OptionsResponse {
  id: number;
  label: string;
}

export const NewProductForm = ({
  mainCategoryValue,
  subCategoryValue,
  subSubCategoryValue,
  productId,
  farmId,
  adminFarmId,
  initialProducerLookup,
  farms,
  isEditMode,
  setAdminFarmId,
}: NewProductFormProps) => {
  const [options, setOptions] = useState([]);
  const [producer, setProducer] = useState<ProducerLookupResponse | null>(null);
  const [producerError, setProducerError] = useState<string | null>(null);
  const [showPersonFields, setShowPersonFields] = useState(false);
  const lookupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user } = useAuth();
  const isAdmin = user?.isAdmin;
  const form = Form.useFormInstance();

  const intl = useIntl();

  const effectiveFarmId = adminFarmId ?? farmId ?? null;
  const selectedQmarkUids: string[] =
    Form.useWatch('producer_qmarks', form) ?? [];

  const certificates = producer?.quality_marks ?? [];

  const producerMarks = certificates.filter(
    (certificate, index) =>
      certificates.findIndex(
        (other) => other.quality_mark_id === certificate.quality_mark_id
      ) === index
  );

  const assignedMarkIds = certificates
    .filter((certificate) =>
      selectedQmarkUids.includes(certificate.registry_uid)
    )
    .map((certificate) => certificate.quality_mark_id);

  useQueryApiClient({
    request: {
      url: productId
        ? `api/v2/products/store/options/${productId}`
        : `api/v2/products/store/options/farm/${effectiveFarmId}`,
    },
    enabled: !!productId || !!effectiveFarmId,
    onSuccess: (response) => {
      setOptions(response.options ?? []);
    },
  });

  const { appendData, isLoading: producerIsLoading } = useQueryApiClient({
    request: {
      url: 'api/v2/producers/lookup',
      disableOnMount: true,
    },
    onSuccess: (response: ProducerLookupResponse, passOnSuccess) => {
      setProducer(response);
      setProducerError(null);
      setShowPersonFields(false);

      if (!passOnSuccess?.fromClear) {
        form.setFieldValue('producer_reg_nr', response.reg_nr ?? '');
      }

      const availableUids = new Set(
        (response.quality_marks ?? []).map((mark) => mark.registry_uid)
      );
      const selected: string[] = form.getFieldValue('producer_qmarks') ?? [];
      const valid = selected.filter((uid) => availableUids.has(uid));

      if (valid.length !== selected.length) {
        form.setFieldValue('producer_qmarks', valid);
      }

      form.setFieldsValue({
        producer_person_name: response.person_name ?? undefined,
        producer_person_surname: response.person_surname ?? undefined,
      });
    },
    onError: (response) => {
      const errorKey = response?.error ?? response?.message;
      const knownErrors = [
        'producer.not_found',
        'producer.svd_inactive',
        'producer.person_name_required',
      ];

      setProducer(null);
      form.setFieldValue('producer_qmarks', []);

      if (errorKey === 'producer.person_name_required') {
        setShowPersonFields(true);
      }

      setProducerError(
        knownErrors.includes(errorKey) ? errorKey : 'producer.lookup_failed'
      );
    },
  });

  const lookupProducer = (
    params: ProducerLookupParams,
    options?: { fromClear?: boolean }
  ) => appendData(params, undefined, { fromClear: !!options?.fromClear });

  useEffect(() => {
    if (producer) {
      form.validateFields(['producer_reg_nr']).catch(() => {});
    }
  }, [producer]);

  useEffect(() => {
    if (isEditMode || !effectiveFarmId) {
      return;
    }

    form.setFieldValue('producer_qmarks', []);
    lookupProducer({ farm_id: effectiveFarmId });
  }, [isEditMode, effectiveFarmId]);

  useEffect(() => {
    if (initialProducerLookup) {
      lookupProducer(initialProducerLookup);
    }
  }, [initialProducerLookup]);

  const handleProducerLookup = () => {
    const value = (form.getFieldValue('producer_reg_nr') ?? '').trim();

    if (value === (producer?.reg_nr ?? '') && !showPersonFields) {
      return;
    }

    const defaultFarmId = isEditMode
      ? (form.getFieldValue('farm_id') ?? effectiveFarmId)
      : effectiveFarmId;

    if (!value) {
      form.setFieldValue('producer_qmarks', []);
      setShowPersonFields(false);
      setProducerError(null);

      if (defaultFarmId) {
        lookupProducer({ farm_id: defaultFarmId }, { fromClear: true });
      } else {
        setProducer(null);
      }

      return;
    }

    if (!REG_NR_PATTERN.test(value)) {
      setProducer(null);
      setProducerError(null);

      return;
    }

    const personName = (
      form.getFieldValue('producer_person_name') ?? ''
    ).trim();
    const personSurname = (
      form.getFieldValue('producer_person_surname') ?? ''
    ).trim();

    if (showPersonFields && (!personName || !personSurname)) {
      return;
    }

    form.setFieldValue('producer_qmarks', []);

    if (!showPersonFields) {
      form.setFieldsValue({
        producer_person_name: undefined,
        producer_person_surname: undefined,
      });
    }

    lookupProducer({
      reg_nr: value,
      name: (showPersonFields && personName) || undefined,
      surname: (showPersonFields && personSurname) || undefined,
    });
  };

  const scheduleProducerLookup = () => {
    if (lookupTimerRef.current) {
      clearTimeout(lookupTimerRef.current);
    }

    lookupTimerRef.current = setTimeout(handleProducerLookup, 700);
  };

  useEffect(
    () => () => {
      if (lookupTimerRef.current) {
        clearTimeout(lookupTimerRef.current);
      }
    },
    []
  );

  return (
    <StyledNewProductForm>
      {isAdmin && !isEditMode && (
        <Select
          name={'farm_id'}
          placeholder={intl.formatMessage({ id: 'general.farms' })}
          labelInValue={true}
          layout={'vertical'}
          label={intl.formatMessage({ id: 'general.farms' })}
          onChange={(value) => setAdminFarmId(value.value)}
        >
          {farms.map((farm: any) => {
            return <SelectOption value={farm.id}>{farm.name}</SelectOption>;
          })}
        </Select>
      )}
      <div className={'name-title '}>
        <Input
          name={'name'}
          type={'text'}
          validations={'requiredText'}
          label={intl.formatMessage({ id: 'general.title' })}
        />
      </div>
      <CategorySelector
        admin
        validations={'required'}
        mainCategoryValue={mainCategoryValue}
        subCategoryValue={subCategoryValue}
        subSubCategoryValue={subSubCategoryValue}
      />

      <div className={'desc'}>
        <TextArea
          name={'description'}
          label={intl.formatMessage({ id: 'general.description' })}
          validations={['required']}
          maxLength={1500}
        />
      </div>

      <div className={'real-time'}>
        <Input
          name={'realisation_time'}
          label={intl.formatMessage({ id: 'general.realisation_time' })}
          validations={'required'}
        />
      </div>

      <div className={'producer'}>
        <div className={'producer-row'}>
          <Input
            name={'producer_reg_nr'}
            label={intl.formatMessage({ id: 'general.producer' })}
            placeholder={intl.formatMessage({
              id: 'general.producer_reg_nr',
            })}
            suffix={producerIsLoading ? <LoadingOutlined spin /> : <span />}
            disabled={producerIsLoading}
            validateTrigger={'onSubmit'}
            rules={[
              {
                validator: async (_: unknown, value: string) => {
                  const trimmed = (value ?? '').trim();
                  if (trimmed && trimmed !== producer?.reg_nr) {
                    throw new Error(
                      intl.formatMessage({ id: 'producer.not_verified' })
                    );
                  }
                },
              },
            ]}
            onChange={scheduleProducerLookup}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleProducerLookup();
              }
            }}
          />
          {showPersonFields && (
            <>
              <Input
                name={'producer_person_name'}
                label={intl.formatMessage({ id: 'general.first_name' })}
                disabled={producerIsLoading}
                onChange={scheduleProducerLookup}
              />
              <Input
                name={'producer_person_surname'}
                label={intl.formatMessage({ id: 'general.surname' })}
                disabled={producerIsLoading}
                onChange={scheduleProducerLookup}
              />
            </>
          )}
        </div>
        {producerIsLoading ? (
          <div className={'producer-feedback'}>
            {intl.formatMessage({ id: 'producer.searching' })}
          </div>
        ) : producerError ? (
          <div className={'producer-feedback error'}>
            {intl.formatMessage({ id: producerError })}
          </div>
        ) : producer?.name ? (
          <div className={'producer-feedback'}>{producer.name}</div>
        ) : null}
      </div>

      <div className={'producer-qmarks'}>
        <Select
          name={'producer_qmarks'}
          mode={'multiple'}
          allowClear
          loading={producerIsLoading}
          label={intl.formatMessage({
            id: 'general.producer_quality_marks',
          })}
        >
          {(producer?.quality_marks ?? []).map((mark) => (
            <SelectOption key={mark.registry_uid} value={mark.registry_uid}>
              {mark.registry_name
                ? `${mark.mark_name} – ${mark.registry_name}`
                : mark.mark_name}
            </SelectOption>
          ))}
        </Select>
      </div>

      <div className={'additional-info'}>
        <div className={'title'}>
          {intl.formatMessage({ id: 'general.additional_information' })}
        </div>
        <div className={'checkboxes'}>
          {producerMarks.length > 0 ? (
            <Checkbox.Group
              className={'readonly-marks'}
              value={assignedMarkIds}
            >
              {producerMarks.map((mark) => (
                <Checkbox
                  key={mark.quality_mark_id}
                  value={mark.quality_mark_id}
                  disabled={true}
                  className={'readonly-mark'}
                >
                  {mark.mark_name}
                </Checkbox>
              ))}
            </Checkbox.Group>
          ) : null}

          {options.length > 0 ? (
            <Form.Item name={'options'}>
              <Checkbox.Group>
                {options.map((option: OptionsResponse, index: number) => (
                  <Checkbox value={option.id} name={'option'} key={index}>
                    {option.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          ) : null}
        </div>
      </div>
    </StyledNewProductForm>
  );
};
